import { useState, useEffect, useRef } from "react";
import { G, ADMIN_API, LEAD_STATUSES, LEAD_STATUS_COLORS } from "../../design/tokens";
import ConversationModal from "./ConversationModal";

export default function LeadModal({ lead, authHeader, loggedUser, onClose, onSaved }) {
  const [status, setStatus]     = useState(lead.status || "New");
  const [newComment, setNewComment] = useState("");
  const [comments, setComments] = useState(() => {
    try { return JSON.parse(lead.comments || "[]"); } catch { return lead.comment ? [{ author: "Admin", text: lead.comment, ts: lead.updatedAt || lead.createdAt }] : []; }
  });
  const [saving, setSaving]     = useState(false);
  const [posting, setPosting]   = useState(false);
  const [saveErr, setSaveErr]   = useState("");
  const [showConvo, setShowConvo] = useState(false);
  const commentsEndRef           = useRef(null);

  useEffect(() => { commentsEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [comments]);

  const saveStatus = async (newStatus) => {
    setStatus(newStatus); setSaveErr("");
    setSaving(true);
    const res  = await fetch(`${ADMIN_API}/leads/${encodeURIComponent(lead.leadId)}`, {
      method: "PUT", headers: authHeader, body: JSON.stringify({ status: newStatus, comments: JSON.stringify(comments) }),
    });
    const data = await res.json();
    setSaving(false);
    if (!res.ok) return setSaveErr(data.error || "Failed");
    onSaved({ ...lead, status: newStatus, comments: JSON.stringify(comments) });
  };

  const postComment = async () => {
    if (!newComment.trim()) return;
    const entry = { author: loggedUser || "Admin", text: newComment.trim(), ts: new Date().toISOString() };
    const updated = [...comments, entry];
    setPosting(true); setSaveErr("");
    const res  = await fetch(`${ADMIN_API}/leads/${encodeURIComponent(lead.leadId)}`, {
      method: "PUT", headers: authHeader, body: JSON.stringify({ status, comments: JSON.stringify(updated) }),
    });
    const data = await res.json();
    setPosting(false);
    if (!res.ok) return setSaveErr(data.error || "Failed");
    setComments(updated);
    setNewComment("");
    onSaved({ ...lead, status, comments: JSON.stringify(updated) });
  };

  const infoRow = (label, value) => (
    <div style={{ display: "flex", gap: 16, padding: "9px 0", borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
      <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 11, color: "rgba(255,255,255,0.3)", textTransform: "uppercase", letterSpacing: 1.2, width: 110, flexShrink: 0, paddingTop: 1 }}>{label}</div>
      <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 13, color: "#fff" }}>{value || "—"}</div>
    </div>
  );

  const timeAgo = (ts) => {
    const diff = (Date.now() - new Date(ts).getTime()) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff/60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff/3600)}h ago`;
    return new Date(ts).toLocaleDateString();
  };

  return (
    <>
      {showConvo && <ConversationModal lead={lead} onClose={() => setShowConvo(false)} />}
      <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", backdropFilter: "blur(8px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
      <div style={{ background: "linear-gradient(145deg, #0F2548 0%, #060E1E 100%)", border: "1px solid rgba(37,99,235,0.2)", borderRadius: 20, width: 560, boxShadow: "0 32px 100px rgba(0,0,0,0.7)", maxHeight: "90vh", display: "flex", flexDirection: "column", overflow: "hidden" }}>

        <div style={{ overflowY: "auto", padding: "32px 32px 0" }}>
          {/* Header */}
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 22 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
              <div style={{ width: 48, height: 48, borderRadius: 14, background: "linear-gradient(135deg, rgba(37,99,235,0.3), rgba(124,58,237,0.3))", border: "1px solid rgba(37,99,235,0.3)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, color: "#93C5FD", fontWeight: 700, fontFamily: "'Space Grotesk', sans-serif" }}>
                {(lead.name || "?")[0].toUpperCase()}
              </div>
              <div>
                <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 22, fontWeight: 600, color: "#fff" }}>{lead.name}</div>
                <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 11, color: "rgba(255,255,255,0.3)", marginTop: 3 }}>
                  Inquiry #{lead.inquiryNo || "—"} · {lead.createdAt ? new Date(lead.createdAt).toLocaleDateString() : "—"}
                </div>
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              {(() => { try { return JSON.parse(lead.conversation || "[]").length > 0; } catch { return false; } })()
                ? (
                  <button onClick={() => setShowConvo(true)} style={{ display: "flex", alignItems: "center", gap: 6, padding: "7px 14px", borderRadius: 8, border: "1px solid rgba(201,149,60,0.35)", background: "rgba(201,149,60,0.1)", color: "#E8C777", fontFamily: "'Space Grotesk', sans-serif", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                    View Conversation
                  </button>
                ) : (
                  <span style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 11, color: "rgba(255,255,255,0.2)", padding: "7px 10px", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 8 }}>No conversation yet</span>
                )
              }
              <button onClick={onClose} style={{ width: 32, height: 32, borderRadius: 9, border: "none", background: "rgba(255,255,255,0.06)", color: "rgba(255,255,255,0.4)", cursor: "pointer", fontSize: 18, display: "flex", alignItems: "center", justifyContent: "center" }}>×</button>
            </div>
          </div>

          {/* Info */}
          <div style={{ background: "rgba(255,255,255,0.02)", borderRadius: 12, padding: "4px 16px", marginBottom: 20 }}>
            {infoRow("Mobile", lead.mobile)}
            {infoRow("Email", lead.email)}
            {infoRow("Captured", lead.createdAt ? new Date(lead.createdAt).toLocaleString() : null)}
            <div style={{ display: "flex", gap: 16, padding: "9px 0", borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
              <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 11, color: "rgba(255,255,255,0.3)", textTransform: "uppercase", letterSpacing: 1.2, width: 110, flexShrink: 0, paddingTop: 1 }}>Source</div>
              <div>{(() => {
                const src = lead.source;
                if (!src) return <span style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 13, color: "#fff" }}>—</span>;
                const isChat = src === "chatbot";
                const isForm = src === "website-contact";
                const color  = isChat ? "#06B6D4" : isForm ? "#7C3AED" : "rgba(255,255,255,0.3)";
                const icon   = isChat ? "💬" : isForm ? "📋" : "";
                const label  = isChat ? "ChatBot" : isForm ? "Form" : src;
                return <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 10px", borderRadius: 6, background: `${color}14`, border: `1px solid ${color}33`, fontFamily: "'Space Grotesk', sans-serif", fontSize: 11, fontWeight: 600, color, letterSpacing: 0.5 }}>{icon} {label}</span>;
              })()}</div>
            </div>
          </div>

          {/* Status */}
          <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 10, color: "rgba(255,255,255,0.3)", letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 10 }}>
            Status {saving && <span style={{ color: "#93C5FD", marginLeft: 8 }}>Saving…</span>}
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 7, marginBottom: 24 }}>
            {LEAD_STATUSES.map(s => {
              const c = LEAD_STATUS_COLORS[s];
              const active = status === s;
              return (
                <button key={s} onClick={() => saveStatus(s)} style={{ padding: "6px 14px", borderRadius: 8, border: `1px solid ${active ? c.border : "rgba(255,255,255,0.08)"}`, background: active ? c.bg : "transparent", color: active ? c.text : "rgba(255,255,255,0.35)", fontFamily: "'Space Grotesk', sans-serif", fontSize: 12, cursor: "pointer", fontWeight: active ? 600 : 400, transition: "all 0.15s" }}>
                  {s}
                </button>
              );
            })}
          </div>

          {/* Comment trail */}
          <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 10, color: "rgba(255,255,255,0.3)", letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 12 }}>Activity / Comments</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 14, maxHeight: 240, overflowY: "auto", paddingRight: 4 }}>
            {comments.length === 0 ? (
              <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 13, color: "rgba(255,255,255,0.2)", textAlign: "center", padding: "20px 0" }}>No comments yet</div>
            ) : comments.map((c, i) => (
              <div key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                <div style={{ width: 30, height: 30, borderRadius: "50%", background: "linear-gradient(135deg, rgba(37,99,235,0.35), rgba(124,58,237,0.35))", border: "1px solid rgba(37,99,235,0.3)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, color: "#93C5FD", fontWeight: 700, flexShrink: 0, fontFamily: "'Space Grotesk', sans-serif" }}>
                  {(c.author || "A")[0].toUpperCase()}
                </div>
                <div style={{ flex: 1, background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.05)", borderRadius: "4px 12px 12px 12px", padding: "10px 14px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 5 }}>
                    <span style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 12, color: "#93C5FD", fontWeight: 600 }}>{c.author}</span>
                    <span style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 11, color: "rgba(255,255,255,0.25)" }}>{timeAgo(c.ts)}</span>
                  </div>
                  <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 13, color: "rgba(255,255,255,0.8)", lineHeight: 1.6, whiteSpace: "pre-wrap" }}>{c.text}</div>
                </div>
              </div>
            ))}
            <div ref={commentsEndRef} />
          </div>

          {saveErr && <div style={{ color: "#f87171", fontFamily: "'Space Grotesk', sans-serif", fontSize: 13, marginBottom: 12, padding: "10px 14px", background: "rgba(248,113,113,0.08)", borderRadius: 9, border: "1px solid rgba(248,113,113,0.15)" }}>{saveErr}</div>}
        </div>

        {/* Sticky comment input */}
        <div style={{ padding: "14px 32px 24px", borderTop: "1px solid rgba(255,255,255,0.05)", background: "rgba(6,14,30,0.6)", flexShrink: 0 }}>
          <div style={{ display: "flex", gap: 10, alignItems: "flex-end" }}>
            <div style={{ width: 30, height: 30, borderRadius: "50%", background: G.btnGrad, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, color: "#fff", fontWeight: 700, flexShrink: 0, fontFamily: "'Space Grotesk', sans-serif" }}>
              {(loggedUser || "A")[0].toUpperCase()}
            </div>
            <textarea value={newComment} onChange={e => setNewComment(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); postComment(); } }}
              placeholder="Write a comment… (Enter to post, Shift+Enter for new line)"
              rows={2}
              style={{ flex: 1, padding: "10px 14px", borderRadius: 10, border: "1px solid rgba(255,255,255,0.08)", background: "rgba(255,255,255,0.04)", color: "#fff", fontFamily: "'Space Grotesk', sans-serif", fontSize: 13, lineHeight: 1.6, outline: "none", resize: "none", boxSizing: "border-box" }}
            />
            <button onClick={postComment} disabled={posting || !newComment.trim()} style={{ padding: "10px 18px", borderRadius: 10, border: "none", background: G.btnGrad, color: "#fff", fontFamily: "'Space Grotesk', sans-serif", fontSize: 13, fontWeight: 600, cursor: "pointer", boxShadow: G.btnShadow, opacity: (posting || !newComment.trim()) ? 0.5 : 1, flexShrink: 0, height: 44 }}>
              {posting ? "…" : "Post"}
            </button>
          </div>
        </div>
      </div>
    </div>
    </>
  );
}
